import { GridFilterItem, GridFilterOperator } from '@mui/x-data-grid-pro';
import { IntlShape } from 'react-intl';
import { formatTranslation } from 'intl';
import { FiltersTranslationKeys } from 'intl/keys';
import { isStringArray } from 'components/common/dataGrid/filterGuards';
import { GridFilterValue } from 'components/common/dataGrid/filters/operators';
import { LastDecisionFilterInput } from '../lastDecisionFilter';

interface LastDecisionRow {
  lastDecisionStatus?: string | null;
}

/**
 * "Zawiera" (Contains) operator for the Last decision column.
 *
 * Uses `GridFilterValue.IS_ANY_OF` ('isAnyOf') as its value - NOT the
 * codebase's usual `CustomFilterOperator.CONTAINS` - because the field's
 * `filterType: 'string'` mapping (see `issueFieldMappingConfig.ts`) routes
 * the filter through the service worker's string-operator branch, which
 * only treats `item.value` as a multi-select array when `operator === 'isAnyOf'`.
 */
export const getLastDecisionContainsOperator = (
  intl: IntlShape
): GridFilterOperator => ({
  label: formatTranslation(intl, FiltersTranslationKeys.operatorContains),
  value: GridFilterValue.IS_ANY_OF,
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (!isStringArray(filterItem.value) || filterItem.value.length === 0) {
      return null;
    }

    const selectedStatuses = new Set(filterItem.value.filter(Boolean));

    if (!selectedStatuses.size) {
      return null;
    }

    return (_: unknown, row: LastDecisionRow) =>
      Boolean(
        row.lastDecisionStatus && selectedStatuses.has(row.lastDecisionStatus)
      );
  },
  InputComponent: LastDecisionFilterInput,
});

/**
 * "Nie dotyczy" (N/A) operator for the Last decision column, matching the
 * N/A case (issue's workflow has no approval-required stages).
 *
 * This is intentionally NOT labelled "is empty": that label would suggest it
 * matches `LastDecisionStatus.NONE` ("Brak decyzji" / no decision made yet on
 * a workflow that *does* require approval), which is a distinct state and is
 * already selectable via the "Contains" operator above.
 */
export const getLastDecisionIsEmptyOperator = (
  intl: IntlShape
): GridFilterOperator => ({
  label: formatTranslation(intl, FiltersTranslationKeys.operatorIsNotApplicable),
  value: GridFilterValue.IS_EMPTY,
  requiresFilterValue: false,
  getApplyFilterFn: () => (_: unknown, row: LastDecisionRow) =>
    row.lastDecisionStatus === null || row.lastDecisionStatus === undefined,
});
